import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import ServerPath from '@configs/serverPath';

// ── local hooks ────────────────────────────────────────────────────────────────

const useCountdownDiff = (targetMs) => {
    const [diff, setDiff] = useState(null);

    useEffect(() => {
        if (targetMs === null) return;

        const initial = targetMs - Date.now();
        if (initial <= 0) return;

        setDiff(initial);

        let timerId;
        const tick = () => {
            const remaining = targetMs - Date.now();
            if (remaining <= 0) {
                setDiff(null);
                return;
            }
            const nextDelay = remaining % 1000 + 1;
            setDiff(remaining);
            timerId = setTimeout(tick, nextDelay);
        };

        timerId = setTimeout(tick, initial % 1000 + 1);
        return () => clearTimeout(timerId);
    }, [targetMs]);

    return diff;
};

const DAY_MS  = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MIN_MS  = 60 * 1000;
const SEC_MS  = 1000;
const pad = (n) => String(n).padStart(2, '0');

const computeCountdownParts = (diff) => {
    return {
        day:  pad(Math.floor(diff / DAY_MS)),
        hour: pad(Math.floor((diff % DAY_MS) / HOUR_MS)),
        min:  pad(Math.floor((diff % HOUR_MS) / MIN_MS)),
        sec:  pad(Math.floor((diff % MIN_MS) / SEC_MS)),
    };
};

// ── UI ─────────────────────────────────────────────────────────────────────────

const WorldCupCountdownUI = ({ tournamentKey, titleKey, imageName, day, hour, min, sec }) => {
    const { t } = useTranslation('common');
    const { composePath } = useSiteInfo();
    const eventTitle = t(`lbl_${titleKey}`);

    return (
        <div className="event-countdown" data-event={tournamentKey}>
            <div className="event-countdown__main">
                <img className="event-logo" src={composePath({ pathType: ServerPath.globalImage, pathName: imageName })} alt="" />
                <span className="event-countdown__title">{eventTitle}</span>
                <div className="countdown">
                    <div className="countdown__col-day"><span className="countdown__time">{day}</span><span className="countdown__text">DAYS</span></div>
                    <div className="countdown__col-hrs"><span className="countdown__time">{hour}</span><span className="countdown__text">HRS</span></div>
                    <div className="countdown__col-min"><span className="countdown__time">{min}</span><span className="countdown__text">MIN</span></div>
                    <div className="countdown__col-sec"><span className="countdown__time">{sec}</span><span className="countdown__text">SEC</span></div>
                </div>
            </div>
        </div>
    );
};

// ── registry ───────────────────────────────────────────────────────────────────

const TOURNAMENT_ID = {
    WorldCup: 40,
};

const TOURNAMENT_REGISTRY = {
    [TOURNAMENT_ID.WorldCup]: {
        key: 'world-cup',
        titleKey: 'WorldCup',
        imageName: 'event-countdown/2026/world-cup/event-logo.png',
        component: WorldCupCountdownUI,
    },
};

// ── component ──────────────────────────────────────────────────────────────────

const SportsGameCountdown = () => {
    const { tournamentCountdownSettings } = useSiteInfo();

    const id = Object.keys(tournamentCountdownSettings ?? {})[0];
    const endTime = id ? tournamentCountdownSettings[id]?.endTime : null;
    const config = TOURNAMENT_REGISTRY[id];

    const targetMs = (!id || !config || !endTime) ? null : dayjs(`${endTime}-04:00`).valueOf();

    const diff = useCountdownDiff(targetMs);

    if (!config || diff === null) return null;

    const { day, hour, min, sec } = computeCountdownParts(diff);

    const { key, titleKey, imageName, component: TournamentUI } = config;
    return <TournamentUI tournamentKey={key} titleKey={titleKey} imageName={imageName} day={day} hour={hour} min={min} sec={sec} />;
};

export default SportsGameCountdown;
